import { BookOpenCheck, CircleAlert, Eye, RefreshCcw, SearchCheck, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Seo } from '../lib/seo';
import { absoluteUrl } from '../lib/site';

const reviewSteps = [
  {
    icon: SearchCheck,
    title: 'Diagnose before recommending',
    description: 'We separate resolution, blur, noise, compression, physical damage, lighting and background problems before suggesting a workflow.'
  },
  {
    icon: ShieldCheck,
    title: 'Preserve the source',
    description: 'Guidance starts from the highest-quality original available and treats the untouched source as the reference for every comparison.'
  },
  {
    icon: Eye,
    title: 'Review high-risk details',
    description: 'Faces, text, logos, product edges, hair, fabric and repeated patterns receive extra attention because reconstruction errors are easiest to miss there.'
  },
  {
    icon: RefreshCcw,
    title: 'Update when the product changes',
    description: 'Supported formats, controls, credit rules, output limits and workflow behavior are review triggers for related guides and help content.'
  }
];

export function MethodologyPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Nexhance AI Editorial and Image Review Methodology',
    url: absoluteUrl('/methodology'),
    description: 'How Nexhance AI researches, reviews and maintains image-enhancement guidance and explains the limits of AI reconstruction.',
    about: {
      '@type': 'Thing',
      name: 'AI image enhancement quality review'
    }
  };

  return (
    <section className="page-section methodology-page">
      <Seo
        title="Editorial & Image Review Methodology"
        description="See how Nexhance AI reviews image-enhancement guidance, checks high-risk details, documents AI limits and updates content when workflows change."
        path="/methodology"
        jsonLd={schema}
      />
      <div className="shell">
        <div className="page-hero compact-page-hero">
          <p className="eyebrow">Editorial and testing methodology</p>
          <h1>How Nexhance AI reviews image guidance and keeps recommendations useful.</h1>
          <p>Our content is built around visible image problems, conservative processing and repeatable quality checks. This page explains the review method used across guides, tool explanations and support content.</p>
        </div>

        <div className="principles-grid methodology-principles">
          {reviewSteps.map((step) => {
            const Icon = step.icon;
            return <article key={step.title}><Icon /><h2>{step.title}</h2><p>{step.description}</p></article>;
          })}
        </div>

        <section className="about-editorial-section methodology-section">
          <div>
            <p className="eyebrow">A repeatable review process</p>
            <h2>Judge the result against the source, not against a promise.</h2>
          </div>
          <div>
            <p>Image-enhancement guidance is most useful when the source problem is identified first. A noisy low-light photo, a motion-blurred image, a tiny logo and a damaged family scan should not receive the same recommendation. We therefore begin by describing the visible defect and the intended destination before suggesting a tool or scale factor.</p>
            <p>The source image remains the reference. Reviews compare the original and result at matched zoom, then inspect the file again at the size where it will actually be used. This helps separate genuine usability improvements from dramatic changes that only look impressive at one viewing size.</p>
            <ul className="about-editorial-list">
              <li><strong>At normal size:</strong> check whether the subject is easier to read without changing the overall character of the image.</li>
              <li><strong>At 100% zoom:</strong> inspect eyes, teeth, hair, text, logos, product edges, fine fabric, foliage and repeated patterns.</li>
              <li><strong>At delivery size:</strong> confirm that the file has enough pixels for the intended web, print, marketplace or design use without unnecessary enlargement.</li>
              <li><strong>Against the original:</strong> treat reconstructed detail as an estimate when the source did not clearly record that information.</li>
            </ul>
          </div>
        </section>

        <section className="about-editorial-section methodology-section">
          <div>
            <p className="eyebrow">What we do not claim</p>
            <h2>Sharper-looking output is not automatically more accurate.</h2>
          </div>
          <div>
            <p>AI enhancement can reconstruct plausible texture, edges, facial features or lighting. A result may therefore look cleaner while still containing details that were estimated rather than recovered from the original recording. Nexhance AI does not present those reconstructions as forensic evidence, identity proof or historically exact information.</p>
            <p>For identity-sensitive, legal, medical, archival or product-accuracy work, human review remains necessary. When a source is too damaged to support a reliable conclusion, the guidance should say so instead of implying that a stronger setting can reveal hidden truth.</p>
            <div className="methodology-callout"><CircleAlert size={20} /><p><strong>Practical rule:</strong> if a detail matters because it proves who, what, when or how, verify it from the original source or another reliable record rather than from an AI reconstruction alone.</p></div>
          </div>
        </section>

        <section className="about-editorial-section methodology-section">
          <div>
            <p className="eyebrow">Content maintenance</p>
            <h2>Update guidance when the product or evidence changes.</h2>
          </div>
          <div>
            <p>Guides display update dates and are reviewed when supported formats, controls, credit behavior, output limits or processing workflows change. We also revise content when a recommendation is unclear, too broad or no longer matches the actual product.</p>
            <p>Corrections should improve the page rather than hide the limitation. If you notice a technical error, broken workflow or misleading explanation, use the public support form and include the page URL and the specific issue.</p>
            <div className="about-plan-links">
              <Link to="/guides" className="button button-primary"><BookOpenCheck size={17} /> Browse guides</Link>
              <Link to="/contact" className="button button-ghost">Report a content issue</Link>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
}
